import { cn } from '@client/lib';
import { formatCurrency } from '@shared';
import { TrendingUp } from 'lucide-react';
import { ASSET_OPTIONS } from './AssetSelectionGrid';

interface ComparisonCardData {
  remainingAssets: number;
  totalCoverage: number;
  coveragePercent: number;
  surplus: number;
  gap: number;
  policyCoverage?: number;
}

interface ComparisonCardProps {
  title: string;
  subtitle: string;
  data: ComparisonCardData;
  variant?: 'default' | 'highlighted';
  badge?: string;
}

function ComparisonCard({
  title,
  subtitle,
  data,
  variant = 'default',
  badge,
}: ComparisonCardProps) {
  const isHighlighted = variant === 'highlighted';

  return (
    <div
      className={cn('relative flex flex-col overflow-hidden rounded-2xl', {
        'border border-gray-200 bg-white shadow-sm': !isHighlighted,
        'border-primary border-2 bg-white shadow-sm': isHighlighted,
      })}
    >
      {/* Badge */}
      {badge && (
        <div className="absolute right-0 top-0">
          <span className="bg-primary inline-block rounded-bl-xl px-3 py-1.5 text-xs font-semibold text-white">
            {badge}
          </span>
        </div>
      )}

      {/* Header */}
      <div className="px-5 pb-3 pt-5">
        <h3 className="text-lg font-bold text-gray-900">{title}</h3>
        <p className="mt-0.5 text-sm text-gray-500">{subtitle}</p>
      </div>

      {/* Hero Metric Section */}
      <div
        className={cn('mx-4 rounded-xl p-4', {
          'bg-gray-50': !isHighlighted,
          'bg-gray-50 shadow-sm': isHighlighted,
        })}
      >
        <dl
          className={cn('flex flex-col gap-3', {
            'sm:flex-row sm:justify-between sm:gap-4': data.policyCoverage !== undefined,
          })}
        >
          <div className="flex-1">
            <dt className="text-xs font-semibold uppercase tracking-wide text-gray-500">
              Remaining Assets after Projected LTC Event
            </dt>
            <dd
              className={cn('mt-1 font-sans text-2xl font-bold tabular-nums', {
                'text-rose-500': data.remainingAssets < 0,
                'text-green-500': data.remainingAssets >= 0,
              })}
            >
              {formatCurrency(data.remainingAssets)}
            </dd>
          </div>
        </dl>
      </div>

      {/* Summary Metrics Grid */}
      <div className="mx-4 mb-4 mt-3 grid grid-cols-2 gap-3">
        <div className="rounded-lg bg-gray-50 p-3">
          <dt className="text-xs font-medium text-gray-500">Total LTC Coverage</dt>
          <dd className="mt-1 flex flex-wrap items-baseline gap-1.5 font-sans">
            <span className="text-base font-semibold tabular-nums text-green-500">
              {formatCurrency(data.totalCoverage)}
            </span>
            <span className="inline-flex items-center rounded-full px-2 py-0.5 text-xs font-semibold text-gray-700">
              {data.coveragePercent}%
            </span>
          </dd>
        </div>
        {data.surplus > 0 ? (
          <div className="rounded-lg bg-gray-50 p-3">
            <dt className="text-xs font-medium text-gray-500">LTC Coverage Gap</dt>
            <dd className="mt-1 flex items-center gap-1.5 font-sans text-base font-semibold text-green-600">
              <span>✓</span>
              <span>Fully Covered</span>
            </dd>
          </div>
        ) : data.gap > 0 ? (
          <div className="rounded-lg bg-gray-50 p-3">
            <dt className="text-xs font-medium text-gray-500">LTC Coverage Gap</dt>
            <dd className="mt-1 font-sans text-base font-semibold tabular-nums text-red-600">
              -{formatCurrency(data.gap)}
            </dd>
          </div>
        ) : (
          <div className="rounded-lg bg-green-50 p-3">
            <dt className="text-xs font-medium text-gray-500">LTC Coverage Gap</dt>
            <dd className="mt-1 flex items-center gap-1.5 font-sans text-base font-semibold text-green-600">
              <span>✓</span>
              <span>Fully Covered</span>
            </dd>
          </div>
        )}
      </div>
    </div>
  );
}

interface ComparisonData {
  withoutInsurance: {
    remainingAssets: number;
    totalCoverage: number;
    coveragePercent: number;
    surplus: number;
    gap: number;
  };
  withInsurance: {
    remainingAssets: number;
    policyCoverage: number;
    totalCoverage: number;
    coveragePercent: number;
    surplus: number;
    gap: number;
  };
}

interface PlanComparisonViewProps {
  projectedLtcCost: number;
  comparison: ComparisonData;
  selectedPolicy: {
    name: string;
    coverage: number;
  };
  fundingBreakdown: Array<{
    assetType: LtcFinancialPlanningAssetType;
    amountIn: number;
    projectedValue: number;
    roi: number;
  }>;
  protectedAssets: Array<{
    assetType: LtcFinancialPlanningAssetType;
    amountUsed: number;
    futureValue: number;
  }>;
  totalPolicyCost: number;
  netDifference: number;
  className?: string;
}

export function PlanComparisonView({
  projectedLtcCost,
  comparison,
  selectedPolicy,
  fundingBreakdown,
  protectedAssets,
  totalPolicyCost,
  netDifference,
  className,
}: PlanComparisonViewProps) {
  return (
    <div className={cn('space-y-6', className)}>
      {/* LTC Cost Header */}
      <div className="text-center">
        <p className="text-sm text-gray-500">Your Projected LTC Cost</p>
        <p className="text-primary text-3xl font-bold">{formatCurrency(projectedLtcCost)}</p>
      </div>

      {/* Comparison Cards */}
      <div className="grid gap-4 md:grid-cols-2">
        <ComparisonCard
          title="Without Insurance"
          subtitle="Self-funding only"
          data={comparison.withoutInsurance}
          variant="default"
        />
        <ComparisonCard
          title="With Insurance"
          subtitle={selectedPolicy.name}
          data={comparison.withInsurance}
          variant="highlighted"
          badge="Latest Update"
        />
      </div>

      {/* Net Difference */}
      <div className="rounded-xl bg-gray-50 px-4 py-8 text-center">
        <TrendingUp className="mx-auto mb-4 size-8 text-gray-600" />
        <div className="space-y-2">
          <p className="text-gray-600">With this plan, you'll have</p>
          <p
            className={cn('text-2xl font-bold', {
              'text-green-600': netDifference >= 0,
              'text-red-600': netDifference < 0,
            })}
          >
            {netDifference >= 0 ? '+' : ''}
            {formatCurrency(netDifference)}
          </p>
          <p className="text-sm text-gray-500">
            more dollars than you would have had without protection
          </p>
        </div>
      </div>

      {/* Funding Breakdown */}
      <div className="rounded-2xl border border-gray-200 bg-white p-5">
        <h3 className="mb-4 text-base font-semibold text-gray-900">Funding Breakdown</h3>

        <div className="space-y-3">
          {fundingBreakdown.map(item => {
            const assetLabel =
              ASSET_OPTIONS.find(a => a.type === item.assetType)?.label || item.assetType;
            return (
              <div
                key={item.assetType}
                className="flex items-center justify-between rounded-lg bg-gray-50 px-3 py-2.5"
              >
                <div className="min-w-0 flex-1">
                  <p className="font-medium text-gray-900">{assetLabel}</p>
                  <p className="mt-0.5 text-sm text-gray-600">
                    <span className="tabular-nums">{formatCurrency(item.amountIn)}</span>
                    <span className="mx-1.5 text-gray-400">→</span>
                    <span className="tabular-nums">{formatCurrency(item.projectedValue)}</span>{' '}
                    projected <span className="text-primary font-medium">(+{item.roi}% ROI)</span>
                  </p>
                </div>
                <p className="ml-4 shrink-0 font-semibold tabular-nums text-gray-900">
                  {formatCurrency(item.amountIn)}
                </p>
              </div>
            );
          })}

          <div className="mt-4 flex items-center justify-between border-t border-gray-200 pt-4">
            <p className="text-base font-semibold text-gray-900">Total Policy Cost</p>
            <p className="text-lg font-bold tabular-nums text-gray-900">
              {formatCurrency(totalPolicyCost)}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
